'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, Filter, MapPin, Trophy, BookmarkPlus, GitCompare, Building2, ArrowRight, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

interface College {
  id: string;
  name: string;
  shortName: string;
  city: string;
  state: string;
  type: 'Government' | 'Private' | 'Deemed' | 'Central';
  nirf: number | null;
  naac: string;
  mbbsSeats: number;
  annualFees: number;
  established: number;
  cutoffAIQ: string;
  hostel: boolean;
  affiliated: string;
  phone: string;
  email: string;
  website: string;
  tags: string[];
  slug: string;
}

const allColleges: College[] = [
  { id: 'col-001', slug: 'aiims-new-delhi', name: 'All India Institute of Medical Sciences, New Delhi', shortName: 'AIIMS New Delhi', city: 'New Delhi', state: 'Delhi', type: 'Central', nirf: 1, naac: 'A++', mbbsSeats: 107, annualFees: 1628, established: 1956, cutoffAIQ: '50–80', hostel: true, affiliated: 'Autonomous', phone: '+91 11 2658 8500', email: 'director@aiims.edu', website: 'aiims.edu', tags: ['Top NIRF', 'AIIMS', 'Central Govt'] },
  { id: 'col-002', slug: 'maulana-azad-medical-college', name: 'Maulana Azad Medical College', shortName: 'MAMC Delhi', city: 'New Delhi', state: 'Delhi', type: 'Government', nirf: 3, naac: 'A+', mbbsSeats: 250, annualFees: 28900, established: 1958, cutoffAIQ: '300–350', hostel: true, affiliated: 'University of Delhi', phone: '+91 11 2323 2400', email: 'info@mamc.ac.in', website: 'mamc.ac.in', tags: ['Top 5', 'Delhi Govt'] },
  { id: 'col-003', slug: 'seth-gs-medical-college-kem', name: 'Seth GS Medical College & KEM Hospital', shortName: 'KEM Mumbai', city: 'Mumbai', state: 'Maharashtra', type: 'Government', nirf: 5, naac: 'A+', mbbsSeats: 200, annualFees: 42300, established: 1926, cutoffAIQ: '850–1100', hostel: true, affiliated: 'Mumbai University', phone: '+91 22 2410 7000', email: 'dean@kem.edu', website: 'kem.edu', tags: ['Top 10', 'Maharashtra Govt'] },
  { id: 'col-004', slug: 'christian-medical-college-vellore', name: 'Christian Medical College', shortName: 'CMC Vellore', city: 'Vellore', state: 'Tamil Nadu', type: 'Private', nirf: 4, naac: 'A++', mbbsSeats: 100, annualFees: 85000, established: 1900, cutoffAIQ: '500–1200', hostel: true, affiliated: 'Autonomous', phone: '+91 416 228 1000', email: 'registrar@cmcvellore.ac.in', website: 'cmcvellore.ac.in', tags: ['Top 5', 'Private', 'Christian'] },
  { id: 'col-005', slug: 'armed-forces-medical-college', name: 'Armed Forces Medical College', shortName: 'AFMC Pune', city: 'Pune', state: 'Maharashtra', type: 'Government', nirf: 7, naac: 'A+', mbbsSeats: 150, annualFees: 56500, established: 1948, cutoffAIQ: '200–430', hostel: true, affiliated: 'University of Pune', phone: '+91 20 2633 3333', email: 'commandant@afmc.nic.in', website: 'afmc.nic.in', tags: ['Defence', 'Top 10'] },
  { id: 'col-006', slug: 'vmmc-safdarjung-hospital', name: 'VMMC & Safdarjung Hospital', shortName: 'VMMC Delhi', city: 'New Delhi', state: 'Delhi', type: 'Central', nirf: 6, naac: 'A+', mbbsSeats: 150, annualFees: 35000, established: 1954, cutoffAIQ: '450–680', hostel: true, affiliated: 'Guru Gobind Singh IP University', phone: '+91 11 2616 5060', email: 'vmmc@nic.in', website: 'vmmc-sjh.edu.in', tags: ['Central Govt', 'Delhi'] },
  { id: 'col-007', slug: 'madras-medical-college', name: 'Madras Medical College', shortName: 'MMC Chennai', city: 'Chennai', state: 'Tamil Nadu', type: 'Government', nirf: 9, naac: 'A', mbbsSeats: 250, annualFees: 14500, established: 1835, cutoffAIQ: '1800–2400', hostel: true, affiliated: 'The Tamil Nadu Dr. MGR Medical University', phone: '+91 44 2530 5000', email: 'dean.mmc@tn.gov.in', website: 'mmc.ac.in', tags: ['Oldest', 'Tamil Nadu Govt'] },
  { id: 'col-008', slug: 'grant-medical-college-jj-hospital', name: 'Grant Medical College & Sir JJ Group of Hospitals', shortName: 'GMC Mumbai', city: 'Mumbai', state: 'Maharashtra', type: 'Government', nirf: 11, naac: 'A', mbbsSeats: 150, annualFees: 38700, established: 1845, cutoffAIQ: '1050–1480', hostel: true, affiliated: 'Maharashtra University of Health Sciences', phone: '+91 22 2373 5555', email: 'dean@grantmedicalcollege.in', website: 'grantmedicalcollege.in', tags: ['Heritage', 'Maharashtra'] },
  { id: 'col-009', slug: 'bangalore-medical-college-bmcri', name: 'Bangalore Medical College & Research Institute', shortName: 'BMCRI', city: 'Bangalore', state: 'Karnataka', type: 'Government', nirf: 12, naac: 'A', mbbsSeats: 250, annualFees: 55000, established: 1955, cutoffAIQ: '1800–2500', hostel: true, affiliated: 'Rajiv Gandhi University of Health Sciences', phone: '+91 80 2670 1150', email: 'principal@bmcri.ac.in', website: 'bmcri.ac.in', tags: ['Karnataka Govt'] },
  { id: 'col-010', slug: 'kasturba-medical-college-manipal', name: 'Kasturba Medical College, Manipal', shortName: 'KMC Manipal', city: 'Manipal', state: 'Karnataka', type: 'Deemed', nirf: 14, naac: 'A+', mbbsSeats: 250, annualFees: 1900000, established: 1953, cutoffAIQ: '5000–11000', hostel: true, affiliated: 'Manipal Academy of Higher Education', phone: '+91 820 292 2012', email: 'registrar@manipal.edu', website: 'manipal.edu', tags: ['Deemed', 'Top Private'] },
  { id: 'col-011', slug: 'sri-ramachandra-institute', name: 'Sri Ramachandra Institute of Higher Education', shortName: 'SRIHER Chennai', city: 'Chennai', state: 'Tamil Nadu', type: 'Deemed', nirf: 18, naac: 'A++', mbbsSeats: 150, annualFees: 1450000, established: 1985, cutoffAIQ: '6200–9800', hostel: true, affiliated: 'Deemed University', phone: '+91 44 2476 8028', email: 'admissions@sriramachandra.edu.in', website: 'sriramachandra.edu.in', tags: ['Deemed', 'Tamil Nadu'] },
  { id: 'col-012', slug: 'bj-medical-college-ahmedabad', name: 'BJ Medical College', shortName: 'BJMC Ahmedabad', city: 'Ahmedabad', state: 'Gujarat', type: 'Government', nirf: null, naac: 'A', mbbsSeats: 200, annualFees: 48000, established: 1946, cutoffAIQ: '2800–3600', hostel: true, affiliated: 'Gujarat University', phone: '+91 79 2268 3721', email: 'principal@bjmc.ac.in', website: 'bjmc.ac.in', tags: ['Gujarat Govt'] },
  { id: 'col-013', slug: 'king-george-medical-university', name: 'King George Medical University', shortName: 'KGMU Lucknow', city: 'Lucknow', state: 'Uttar Pradesh', type: 'Government', nirf: 16, naac: 'A+', mbbsSeats: 250, annualFees: 42000, established: 1905, cutoffAIQ: '800–1200', hostel: true, affiliated: 'Autonomous', phone: '+91 522 225 7540', email: 'vc@kgmu.org', website: 'kgmu.org', tags: ['UP Govt', 'Heritage'] },
  { id: 'col-014', slug: 'osmania-medical-college', name: 'Osmania Medical College', shortName: 'OMC Hyderabad', city: 'Hyderabad', state: 'Telangana', type: 'Government', nirf: null, naac: 'A', mbbsSeats: 250, annualFees: 28000, established: 1846, cutoffAIQ: '1400–2100', hostel: true, affiliated: 'Kaloji Narayana Rao University of Health Sciences', phone: '+91 40 2460 1066', email: 'principal@osmaniamedicol.ac.in', website: 'osmaniamedicol.ac.in', tags: ['Telangana Govt', 'Heritage'] },
  { id: 'col-015', slug: 'government-medical-college-thiruvananthapuram', name: 'Government Medical College Thiruvananthapuram', shortName: 'GMC Trivandrum', city: 'Thiruvananthapuram', state: 'Kerala', type: 'Government', nirf: null, naac: 'A', mbbsSeats: 200, annualFees: 18000, established: 1951, cutoffAIQ: '1100–1700', hostel: true, affiliated: 'Kerala University of Health Sciences', phone: '+91 471 252 8386', email: 'principal@gmctvm.ac.in', website: 'gmctvm.ac.in', tags: ['Kerala Govt'] },
  { id: 'col-016', slug: 'sms-medical-college-jaipur', name: 'SMS Medical College Jaipur', shortName: 'SMS Jaipur', city: 'Jaipur', state: 'Rajasthan', type: 'Government', nirf: null, naac: 'A', mbbsSeats: 250, annualFees: 35000, established: 1947, cutoffAIQ: '1200–1800', hostel: true, affiliated: 'University of Rajasthan', phone: '+91 141 256 0291', email: 'principal@smsjaipur.ac.in', website: 'smsjaipur.ac.in', tags: ['Rajasthan Govt'] },
];

const states = ['All States', 'Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Gujarat', 'Rajasthan', 'Uttar Pradesh', 'West Bengal', 'Kerala', 'Telangana'];
const types = ['Government', 'Private', 'Deemed', 'Central'];
const naacGrades = ['All', 'A++', 'A+', 'A'];

const formatFees = (fees: number) => {
  if (fees >= 100000) return `₹${(fees / 100000).toFixed(1)}L/yr`;
  return `₹${fees.toLocaleString('en-IN')}/yr`;
};

const TypeBadge = ({ type }: { type: string }) => {
  const cls = type === 'Government' || type === 'Central' ? 'badge-govt' : type === 'Private' ? 'badge-private' : 'badge-deemed';
  return <span className={`${cls} px-2 py-0.5 rounded-md text-xs font-semibold`}>{type}</span>;
};

export default function CollegesDatabaseClient() {
  const [search, setSearch] = useState('');
  const [selectedState, setSelectedState] = useState('All States');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedNaac, setSelectedNaac] = useState('All');
  const [feeRange, setFeeRange] = useState<number>(2500000);
  const [sortBy, setSortBy] = useState<'nirf' | 'fees' | 'seats'>('nirf');
  const [savedColleges, setSavedColleges] = useState<string[]>([]);
  const [compareList, setCompareList] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const perPage = 9;

  const filtered = useMemo(() => {
    return allColleges
      .filter(c =>
        (!search || c.name.toLowerCase().includes(search.toLowerCase()) || c.city.toLowerCase().includes(search.toLowerCase()) || c.shortName.toLowerCase().includes(search.toLowerCase())) &&
        (selectedState === 'All States' || c.state === selectedState) &&
        (selectedTypes.length === 0 || selectedTypes.includes(c.type)) &&
        (selectedNaac === 'All' || c.naac === selectedNaac) &&
        (c.annualFees <= feeRange)
      )
      .sort((a, b) => {
        if (sortBy === 'nirf') return (a.nirf ?? 999) - (b.nirf ?? 999);
        if (sortBy === 'fees') return a.annualFees - b.annualFees;
        if (sortBy === 'seats') return b.mbbsSeats - a.mbbsSeats;
        return 0;
      });
  }, [search, selectedState, selectedTypes, selectedNaac, feeRange, sortBy]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  const toggleType = (t: string) => {
    setSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
    setPage(1);
  };

  const toggleSave = (id: string) => {
    setSavedColleges(prev => {
      if (prev.includes(id)) { toast.info('Removed from saved list'); return prev.filter(x => x !== id); }
      toast.success('College saved to dashboard'); return [...prev, id];
    });
  };

  const toggleCompare = (id: string) => {
    setCompareList(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 4) { toast.error('You can compare up to 4 colleges'); return prev; }
      toast.success('Added to comparison');
      return [...prev, id];
    });
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 glass-card rounded-full text-xs text-success border border-success/20 mb-4 font-semibold">
          <Building2 size={13} />
          <span>Medical Colleges Directory</span>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2 tracking-tight">
              Medical <span className="gradient-text">Colleges Database</span>
            </h1>
            <p className="text-muted-foreground text-base">
              2,847 verified medical colleges across India — fees, cutoffs, seats, and more.
            </p>
          </div>
          {compareList.length > 0 && (
            <Link href="/compare-colleges" className="btn-primary flex items-center gap-2 whitespace-nowrap">
              <GitCompare size={16} />
              Compare {compareList.length} Colleges
            </Link>
          )}
        </div>
      </div>

      {/* Search + Filter Bar */}
      <div className="glass-card-white rounded-2xl p-4 mb-6 border border-white/60">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by college name, city..."
              className="input-glass pl-10 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <select value={selectedState} onChange={e => { setSelectedState(e.target.value); setPage(1); }} className="select-glass text-sm py-2.5 px-3 w-auto">
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)} className="select-glass text-sm py-2.5 px-3 w-auto">
              <option value="nirf">Sort: NIRF</option>
              <option value="fees">Sort: Fees</option>
              <option value="seats">Sort: Seats</option>
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${showFilters ? 'bg-primary text-white border-primary' : 'bg-white border-slate-200 text-muted-foreground hover:border-primary/30'}`}
            >
              <SlidersHorizontal size={14} />
              Filters
              {selectedTypes.length > 0 && <span className="w-5 h-5 rounded-full bg-white text-primary text-xs flex items-center justify-center font-bold">{selectedTypes.length}</span>}
            </button>
          </div>
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">College Type</p>
              <div className="flex flex-wrap gap-2">
                {types.map(t => (
                  <button
                    key={t}
                    onClick={() => toggleType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedTypes.includes(t) ? 'bg-primary text-white border-primary' : 'bg-white border-slate-200 text-muted-foreground hover:border-primary/30'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">NAAC Grade</p>
              <div className="flex flex-wrap gap-2">
                {naacGrades.map(g => (
                  <button
                    key={g}
                    onClick={() => setSelectedNaac(g)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedNaac === g ? 'bg-primary text-white border-primary' : 'bg-white border-slate-200 text-muted-foreground hover:border-primary/30'}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Max Annual Fees: {formatFees(feeRange)}</p>
              <input
                type="range"
                min={0}
                max={2500000}
                step={50000}
                value={feeRange}
                onChange={e => setFeeRange(Number(e.target.value))}
                className="w-full accent-primary"
              />
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground mb-4">
        Showing <strong>{filtered.length}</strong> colleges
        {selectedTypes.length > 0 && ` · ${selectedTypes.join(', ')}`}
        {selectedState !== 'All States' && ` · ${selectedState}`}
      </p>

      {/* College Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8">
        {paginated.map(college => (
          <div key={college.id} className="glass-card-hover rounded-2xl p-5 border border-white/50 group">
            {/* College header */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center flex-shrink-0">
                <Building2 size={22} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-foreground leading-tight line-clamp-2">{college.name}</h3>
                <div className="flex items-center gap-1.5 mt-1">
                  <MapPin size={11} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs text-muted-foreground">{college.city}, {college.state}</span>
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              <TypeBadge type={college.type} />
              {college.nirf && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-warning/10 text-warning border border-warning/20">
                  <Trophy size={10} /> NIRF #{college.nirf}
                </span>
              )}
              <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-success/10 text-success border border-success/20">
                NAAC {college.naac}
              </span>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-2 bg-slate-50 rounded-xl">
                <p className="text-sm font-bold text-foreground">{college.mbbsSeats}</p>
                <p className="text-xs text-muted-foreground">Seats</p>
              </div>
              <div className="text-center p-2 bg-slate-50 rounded-xl">
                <p className="text-sm font-bold text-foreground">{formatFees(college.annualFees)}</p>
                <p className="text-xs text-muted-foreground">Fees</p>
              </div>
              <div className="text-center p-2 bg-slate-50 rounded-xl">
                <p className="text-sm font-bold text-foreground">{college.cutoffAIQ}</p>
                <p className="text-xs text-muted-foreground">AIQ Cutoff</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Link href={`/college/${college.slug}`} className="btn-primary flex-1 text-center text-xs py-2.5 justify-center gap-1.5">
                View Details
                <ArrowRight size={12} />
              </Link>
              <button
                onClick={() => toggleSave(college.id)}
                className={`p-2.5 rounded-xl border transition-all ${savedColleges.includes(college.id) ? 'bg-primary/10 border-primary/30 text-primary' : 'border-slate-200 text-muted-foreground hover:border-primary/30 hover:text-primary'}`}
                title="Save college"
              >
                <BookmarkPlus size={15} />
              </button>
              <button
                onClick={() => toggleCompare(college.id)}
                className={`p-2.5 rounded-xl border transition-all ${compareList.includes(college.id) ? 'bg-accent/10 border-accent/30 text-accent' : 'border-slate-200 text-muted-foreground hover:border-accent/30 hover:text-accent'}`}
                title="Add to compare"
              >
                <GitCompare size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary px-4 py-2 text-sm disabled:opacity-40">Previous</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-9 h-9 rounded-xl text-sm font-semibold transition-all ${page === p ? 'btn-primary' : 'btn-secondary'}`}
            >
              {p}
            </button>
          ))}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-secondary px-4 py-2 text-sm disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}